import { useState, useEffect, useMemo, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Target, Zap, TrendingDown, Clock, CheckCircle2, Brain, Signal, Shield, Flame, Eye, BarChart3 } from "lucide-react";
import { cn } from "@/lib/utils";

// ─── Types ───────────────────────────────────────────────────

interface KippunktWindow {
  timeframeMonths: number;
  worstCaseProbability?: string;
}

interface Phase12ConflictInputProps {
  onValueChange: (value: string) => void;
  onStructuredChange?: (data: {
    capacityPercent: number;
    kpiMetric: string;
    kpiFrom: string;
    kpiTo: string;
    impactTimeframe: string;
    tensionScore: number;
    riskAlignmentScore: number;
    riskType: string;
  }) => void;
  onGateStatusChange?: (valid: boolean) => void;
  // System context from onboarding + 1.1
  optionALabel?: string;
  optionBLabel?: string;
  thesisText?: string;
  killerCondition?: string;
  dominantRisk?: string;
  blocker?: string;
  blockerType?: string;
  // NEW: Full onboarding + 1.1 data
  validationPercent?: number;
  strongestSignal?: string;
  initialPreference?: 'a' | 'b' | 'undecided';
  computedImpactLevel?: string;
  urgencyScore?: number;
  worstCase?: string;
  reversibility?: string;
  kippunktWindow?: KippunktWindow;
  onboardingMetric?: string;
  className?: string;
}

const KPI_OPTIONS = [
  { value: "pipeline", label: "Pipeline / Leads" },
  { value: "revenue", label: "Umsatz" },
  { value: "time_to_market", label: "Time-to-Market" },
  { value: "burn_rate", label: "Burn-Rate" },
  { value: "market_share", label: "Marktanteil" },
  { value: "learning_cycles", label: "Lernzyklen / Validierung" },
  { value: "dev_capacity", label: "Entwicklerkapazität" },
];

// ─── Risk Type Classification (deterministic, keyword-based) ─────
type RiskType = 'runway' | 'competition' | 'dev_capacity' | 'sales_cycle' | 'compliance' | 'general';

function classifyRiskType(dominantRisk?: string): RiskType {
  if (!dominantRisk) return 'general';
  const r = dominantRisk.toLowerCase();
  if (r.includes("runway") || r.includes("cash") || r.includes("burn") || r.includes("liquidit") || r.includes("finanz")) return 'runway';
  if (r.includes("sales") || r.includes("pipeline") || r.includes("deal") || r.includes("kund") || r.includes("vertrieb")) return 'sales_cycle';
  if (r.includes("team") || r.includes("kapazit") || r.includes("personal") || r.includes("entwickl") || r.includes("fte")) return 'dev_capacity';
  if (r.includes("markt") || r.includes("wettbewerb") || r.includes("konkurrenz") || r.includes("position")) return 'competition';
  if (r.includes("compliance") || r.includes("regulier") || r.includes("recht") || r.includes("audit")) return 'compliance';
  return 'general';
}

const ALL_KPI_VALUES = KPI_OPTIONS.map(o => o.value);

const RISK_KPI_MAPPING: Record<RiskType, { allowed: string[]; suggested: string[] }> = {
  runway:       { allowed: ["burn_rate", "revenue", "pipeline", "dev_capacity"], suggested: ["burn_rate", "revenue", "pipeline"] },
  sales_cycle:  { allowed: ["pipeline", "revenue", "time_to_market", "market_share"], suggested: ["pipeline", "revenue", "time_to_market"] },
  dev_capacity: { allowed: ["dev_capacity", "time_to_market", "learning_cycles", "burn_rate"], suggested: ["dev_capacity", "time_to_market", "learning_cycles"] },
  competition:  { allowed: ["market_share", "time_to_market", "pipeline", "revenue"], suggested: ["market_share", "time_to_market", "pipeline"] },
  compliance:   { allowed: ["burn_rate", "time_to_market", "revenue", "learning_cycles"], suggested: ["burn_rate", "time_to_market", "revenue"] },
  general:      { allowed: ALL_KPI_VALUES, suggested: ["pipeline", "revenue", "time_to_market"] },
};

const RISK_TYPE_LABELS: Record<RiskType, string> = {
  runway: "Runway / Liquidität",
  sales_cycle: "Sales-Zyklus",
  dev_capacity: "Entwicklungskapazität",
  competition: "Wettbewerb / Marktposition",
  compliance: "Compliance / Regulierung",
  general: "Allgemein",
};

const TIMEFRAME_DAYS: Record<string, number> = {
  "30d": 30,
  "90d": 90,
  "6m": 180,
  "12m": 360,
};

function truncateKippunkt(text: string, max = 60): string {
  if (text.length <= max) return text;
  return text.slice(0, max).trimEnd() + "…";
}

const TIMEFRAME_OPTIONS = [
  { value: "30d", label: "30 Tage" },
  { value: "90d", label: "90 Tage" },
  { value: "6m", label: "6 Monate" },
  { value: "12m", label: "12 Monate" },
];

// ─── Helpers ─────────────────────────────────────────────────

function getValidationLabel(percent: number): { label: string; color: string } {
  if (percent >= 80) return { label: "hoch", color: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30" };
  if (percent >= 40) return { label: "mittel", color: "bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30" };
  return { label: "gering", color: "bg-destructive/20 text-destructive border-destructive/30" };
}

function getImpactBadge(level?: string): { label: string; color: string } | null {
  if (!level) return null;
  const l = level.toLowerCase();
  if (l.includes("existenz")) return { label: "Existenzrelevant", color: "bg-destructive/20 text-destructive border-destructive/30" };
  if (l.includes("strateg")) return { label: "Strategisch", color: "bg-primary/20 text-primary border-primary/30" };
  if (l.includes("taktisch")) return { label: "Taktisch", color: "bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30" };
  return { label: level, color: "bg-muted text-muted-foreground border-border" };
}

function getPreferenceLabel(pref: 'a' | 'b' | 'undecided', labelA: string, labelB: string): string {
  if (pref === 'a') return labelA;
  if (pref === 'b') return labelB;
  return "Unentschieden";
}

// ─── Component ───────────────────────────────────────────────

export const Phase12ConflictInput = ({
  onValueChange,
  onStructuredChange,
  onGateStatusChange,
  optionALabel,
  optionBLabel,
  thesisText,
  killerCondition,
  dominantRisk,
  blocker,
  blockerType,
  validationPercent,
  strongestSignal,
  initialPreference,
  computedImpactLevel,
  urgencyScore,
  worstCase,
  reversibility,
  kippunktWindow,
  onboardingMetric,
  className,
}: Phase12ConflictInputProps) => {
  const labelA = optionALabel || "Option A";
  const labelB = optionBLabel || "Option B";

  // ── State ──────────────────────────────────────────────────
  const [capacityPercent, setCapacityPercent] = useState(50);
  const [sliderTouched, setSliderTouched] = useState(false);
  const [capacityMode, setCapacityMode] = useState<'percent' | 'absolute'>('percent');
  const [absoluteValues, setAbsoluteValues] = useState<{
    developers?: string;
    salesHours?: string;
    budget?: string;
  }>({});
  const [kpiMetric, setKpiMetric] = useState("");
  const [kpiFrom, setKpiFrom] = useState("");
  const [kpiTo, setKpiTo] = useState("");
  const [impactTimeframe, setImpactTimeframe] = useState("");
  const [showAllKpis, setShowAllKpis] = useState(false);

  // ── Risk Type Classification (deterministic) ──────────────
  const riskType = useMemo(() => classifyRiskType(dominantRisk), [dominantRisk]);
  const riskMapping = RISK_KPI_MAPPING[riskType];
  const suggestedKpiKeys = useMemo(() => {
    // Befund 3: When risk is general, use onboarding metric to boost suggestions
    if (riskType === 'general' && onboardingMetric) {
      const m = onboardingMetric.toLowerCase();
      const METRIC_KPI_MAP: Record<string, string> = {
        'umsatz': 'revenue', 'revenue': 'revenue', 'umsatzwachstum': 'revenue',
        'marktanteil': 'market_share', 'market': 'market_share',
        'time-to-market': 'time_to_market', 'geschwindigkeit': 'time_to_market', 'speed': 'time_to_market',
        'kosten': 'burn_rate', 'burn': 'burn_rate', 'cashflow': 'burn_rate',
        'pipeline': 'pipeline', 'leads': 'pipeline', 'kunden': 'pipeline',
        'kapazität': 'dev_capacity', 'team': 'dev_capacity',
      };
      const boostedKpi = Object.entries(METRIC_KPI_MAP).find(([key]) => m.includes(key))?.[1];
      if (boostedKpi && !riskMapping.suggested.includes(boostedKpi)) {
        return [boostedKpi, ...riskMapping.suggested.slice(0, 2)];
      }
    }
    return riskMapping.suggested;
  }, [riskType, onboardingMetric, riskMapping]);

  // ── KPI-Risk Constraint (Hard Gate) ────────────────────────
  const isKpiLinked = useMemo(() => {
    if (!kpiMetric) return true; // no selection yet, don't block
    return riskMapping.allowed.includes(kpiMetric);
  }, [kpiMetric, riskMapping]);

  // ── Timeframe-Kippunkt Constraint ──────────────────────────
  const timeframeConstraint = useMemo(() => {
    if (!kippunktWindow || !impactTimeframe) return { aligned: true, warning: null, hardBlock: false };
    const selectedDays = TIMEFRAME_DAYS[impactTimeframe] || 90;
    const windowDays = kippunktWindow.timeframeMonths * 30;

    // Hard block: 12m horizon with ≤3 month window
    if (impactTimeframe === "12m" && kippunktWindow.timeframeMonths <= 3) {
      return { aligned: false, warning: "Bei einem 3-Monats-Fenster ist ein 12-Monats-Horizont nicht operationalisierbar.", hardBlock: true };
    }
    // Warning: high probability + timeframe exceeds window
    if (kippunktWindow.worstCaseProbability === "high" && selectedDays > windowDays) {
      return { aligned: false, warning: "Dein Kippunkt liegt näher als dein gewählter Zeitrahmen.", hardBlock: false };
    }
    return { aligned: true, warning: null, hardBlock: false };
  }, [impactTimeframe, kippunktWindow]);

  const isTimeframeAligned = timeframeConstraint.aligned;

  // ── Risk Alignment Score (0-3) ─────────────────────────────
  const riskAlignmentScore = useMemo(() => {
    let score = 0;
    if (kpiMetric && riskMapping.allowed.includes(kpiMetric)) score += 1;
    if (isTimeframeAligned) score += 1;
    if (capacityPercent > 50) score += 1;
    return score;
  }, [kpiMetric, riskMapping, isTimeframeAligned, capacityPercent]);

  // ── Capacity from absolute values ──────────────────────────
  const absoluteCapacityFilled = useMemo(() => {
    return !!(absoluteValues.developers?.trim() || absoluteValues.salesHours?.trim() || absoluteValues.budget?.trim());
  }, [absoluteValues]);

  // Auto-compute capacityPercent from absolute (best-effort parse "X von Y")
  useEffect(() => {
    if (capacityMode !== 'absolute') return;
    const devStr = absoluteValues.developers?.trim() || '';
    const match = devStr.match(/(\d+)\s*(?:von|\/)\s*(\d+)/i);
    if (match) {
      const pct = Math.round((parseInt(match[1]) / parseInt(match[2])) * 100);
      setCapacityPercent(Math.min(100, Math.max(0, pct)));
    }
  }, [absoluteValues.developers, capacityMode]);

  // ── Gate Logic ─────────────────────────────────────────────
  const capacityGateOk = capacityMode === 'percent' ? sliderTouched : absoluteCapacityFilled;
  const gateValid = useMemo(() => {
    const baseGate = capacityGateOk && kpiMetric !== "" && kpiFrom.trim() !== "" && kpiTo.trim() !== "" && impactTimeframe !== "";
    // Hard gates: KPI must be linked + no timeframe hard block
    return baseGate && isKpiLinked && !timeframeConstraint.hardBlock;
  }, [capacityGateOk, kpiMetric, kpiFrom, kpiTo, impactTimeframe, isKpiLinked, timeframeConstraint.hardBlock]);

  useEffect(() => {
    onGateStatusChange?.(gateValid);
  }, [gateValid, onGateStatusChange]);

  // ── Tension Score (system + user data) ─────────────────────
  const tensionScore = useMemo(() => {
    let score = 0;
    // User inputs
    if (capacityPercent > 60) score += 2;
    if (capacityPercent > 80) score += 1;
    if (kpiFrom.trim() && kpiTo.trim()) score += 2;
    if (kpiMetric) score += 1;
    if (impactTimeframe === "30d") score += 2;
    else if (impactTimeframe === "90d") score += 1;
    if (blocker && capacityPercent > 60) score += 1;
    // System data enrichment
    if (computedImpactLevel) {
      const l = computedImpactLevel.toLowerCase();
      if (l.includes("existenz")) score += 2;
      else if (l.includes("strateg")) score += 1;
    }
    if (validationPercent !== undefined && validationPercent < 30) score += 1;
    if (urgencyScore !== undefined && urgencyScore >= 8) score += 1;
    if (reversibility && capacityPercent > 60) {
      const r = reversibility.toLowerCase();
      if (r.includes("irreversib") || r.includes("schwer")) score += 1;
    }
    return Math.min(score, 10);
  }, [capacityPercent, kpiFrom, kpiTo, kpiMetric, impactTimeframe, blocker, computedImpactLevel, validationPercent, urgencyScore, reversibility]);

  const tensionLevel = useMemo(() => {
    if (tensionScore >= 6) return "high";
    if (tensionScore >= 3) return "medium";
    return "low";
  }, [tensionScore]);

  // ── Consistency checks ─────────────────────────────────────
  const capacityInconsistency = useMemo(() => {
    // User chose Option A as thesis but allocates < 30% to it
    if (thesisText && sliderTouched && capacityPercent < 30) {
      return `Du setzt auf ${labelA}, weist aber nur ${capacityPercent}% Kapazität zu. Reicht das?`;
    }
    return null;
  }, [thesisText, sliderTouched, capacityPercent, labelA]);

  const biasShiftMessage = useMemo(() => {
    // Initial preference was B but thesis chose A (or vice versa)
    if (!initialPreference || initialPreference === 'undecided' || !thesisText) return null;
    // If initial gut feeling was B but now thesis is for A
    if (initialPreference === 'b') {
      return `Du hast anfangs ${labelB} bevorzugt, dich aber für ${labelA} entschieden. Deine Ressourcenzuweisung sollte diese bewusste Entscheidung widerspiegeln.`;
    }
    return null;
  }, [initialPreference, thesisText, labelA, labelB]);

  // ── Output ─────────────────────────────────────────────────
  useEffect(() => {
    if (!gateValid) {
      onValueChange("");
      return;
    }
    const kpiLabel = KPI_OPTIONS.find(o => o.value === kpiMetric)?.label || kpiMetric;
    const timeLabel = TIMEFRAME_OPTIONS.find(o => o.value === impactTimeframe)?.label || impactTimeframe;
    const parts = [
      `Kapazitätsbindung ${labelA}: ${capacityPercent}%`,
    ];
    if (capacityMode === 'absolute') {
      const absDetails = [
        absoluteValues.developers && `Entwickler: ${absoluteValues.developers}`,
        absoluteValues.salesHours && `Sales: ${absoluteValues.salesHours}`,
        absoluteValues.budget && `Budget: ${absoluteValues.budget}€`,
      ].filter(Boolean);
      if (absDetails.length) parts.push(absDetails.join(', '));
    }
    parts.push(`KPI-Verlust (${kpiLabel}): von ${kpiFrom} auf ${kpiTo}`);
    parts.push(`Spürbar in: ${timeLabel}`);
    onValueChange(parts.join("\n"));
    onStructuredChange?.({ capacityPercent, kpiMetric, kpiFrom, kpiTo, impactTimeframe, tensionScore, riskAlignmentScore, riskType });
  }, [capacityPercent, kpiMetric, kpiFrom, kpiTo, impactTimeframe, gateValid, onValueChange, onStructuredChange, labelA, capacityMode, absoluteValues, tensionScore, riskAlignmentScore, riskType]);

  // ── Slider handler ─────────────────────────────────────────
  const handleSliderChange = useCallback((values: number[]) => {
    setCapacityPercent(values[0]);
    if (!sliderTouched) setSliderTouched(true);
  }, [sliderTouched]);

  // ── Derived display data ───────────────────────────────────
  const hasSystemContext = !!(thesisText || dominantRisk || killerCondition);
  const hasMetaBadges = !!(validationPercent !== undefined || computedImpactLevel || initialPreference);
  const validationInfo = validationPercent !== undefined ? getValidationLabel(validationPercent) : null;
  const impactBadge = getImpactBadge(computedImpactLevel);

  return (
    <div className={cn("space-y-6", className)}>

      {/* ═══ Abschnitt 1: System-generiertes Spannungsfeld ═══ */}
      {(hasSystemContext || hasMetaBadges) && (
        <div className="rounded-xl border-2 border-primary/30 bg-primary/[0.04] overflow-hidden">
          <div className="px-4 py-2.5 bg-primary/10 border-b border-primary/20">
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-primary" />
              <span className="text-xs font-bold text-primary uppercase tracking-widest">
                System erkennt
              </span>
            </div>
          </div>
          <div className="p-4 space-y-3">
            {/* Dilemma summary */}
            <p className="text-sm text-foreground leading-relaxed">
              Deine Entscheidung betrifft ein <span className="font-semibold">{labelA} vs. {labelB}</span>-Dilemma
              {blocker && <span> mit <span className="font-medium text-primary">{blockerType === 'intern' ? 'internen' : 'externen'} Ressourcenengpässen</span></span>}.
            </p>

            {/* ── Meta-Badges row ── */}
            {hasMetaBadges && (
              <div className="flex flex-wrap gap-2">
                {initialPreference && (
                  <Badge variant="outline" className="text-xs gap-1 border-border bg-muted/50">
                    <Eye className="w-3 h-3" />
                    Bauchgefühl: {getPreferenceLabel(initialPreference, labelA, labelB)}
                  </Badge>
                )}
                {impactBadge && (
                  <Badge variant="outline" className={cn("text-xs gap-1", impactBadge.color)}>
                    <Flame className="w-3 h-3" />
                    {impactBadge.label}
                  </Badge>
                )}
                {validationInfo && (
                  <Badge variant="outline" className={cn("text-xs gap-1", validationInfo.color)}>
                    <BarChart3 className="w-3 h-3" />
                    Marktevidenz: {validationPercent}% ({validationInfo.label})
                  </Badge>
                )}
              </div>
            )}

            {/* ── Detail lines ── */}
            {thesisText && (
              <div className="flex items-start gap-2 text-sm">
                <Target className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium text-foreground">These:</span>{" "}
                  <span className="text-foreground/80">{thesisText}</span>
                </div>
              </div>
            )}
            {dominantRisk && (
              <div className="flex items-start gap-2 text-sm">
                <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium text-foreground">Risiko:</span>{" "}
                  <span className="text-foreground/80">{dominantRisk}</span>
                </div>
              </div>
            )}
            {killerCondition && (
              <div className="flex items-start gap-2 text-sm">
                <Zap className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium text-foreground">Kipppunkt:</span>{" "}
                  <span className="text-foreground/80">{killerCondition}</span>
                </div>
              </div>
            )}
            {strongestSignal && (
              <div className="flex items-start gap-2 text-sm">
                <Signal className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium text-foreground">Signal:</span>{" "}
                  <span className="text-foreground/80">{strongestSignal}</span>
                </div>
              </div>
            )}
            {worstCase && (
              <div className="flex items-start gap-2 text-sm">
                <Shield className="w-4 h-4 text-destructive/70 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium text-foreground">Worst-Case:</span>{" "}
                  <span className="text-foreground/80">{worstCase}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ Abschnitt 2: Ressourcen-Zuweisung ═══ */}
      <div className="space-y-6">
        <div className="text-center space-y-1">
          <h3 className="text-lg font-semibold text-foreground">
            Welchen messbaren Preis zahlst du für Fokus?
          </h3>
          <p className="text-sm text-muted-foreground">
            Mache Opportunitätskosten sichtbar – keine Theorie, sondern Zahlen.
          </p>
        </div>

        {/* 1. Kapazitätsbindung */}
        <div className="space-y-3 p-4 rounded-xl border border-border bg-background">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <Label className="text-sm font-semibold text-foreground flex-1 min-w-0">
              {killerCondition ? (
                <>Wie viel Kapazität darf <span className="text-primary">{labelA}</span> maximal binden, bevor <span className="text-destructive font-semibold">{truncateKippunkt(killerCondition)}</span> ausgelöst wird?</>
              ) : (
                <>Wie viel Kapazität bindet <span className="text-primary">{labelA}</span> in 90 Tagen?</>
              )}
            </Label>
            <div className="flex rounded-lg border border-border overflow-hidden flex-shrink-0">
              <button
                type="button"
                onClick={() => setCapacityMode('percent')}
                className={cn(
                  "px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap",
                  capacityMode === 'percent'
                    ? "bg-primary/10 text-primary"
                    : "bg-background text-muted-foreground hover:bg-muted/30"
                )}
              >
                Prozent
              </button>
              <button
                type="button"
                onClick={() => setCapacityMode('absolute')}
                className={cn(
                  "px-3 py-1.5 text-xs font-medium transition-colors border-l border-border whitespace-nowrap",
                  capacityMode === 'absolute'
                    ? "bg-primary/10 text-primary"
                    : "bg-background text-muted-foreground hover:bg-muted/30"
                )}
              >
                Absolut
              </button>
            </div>
          </div>

          {capacityMode === 'percent' ? (
            <div className="space-y-2">
              <Slider
                value={[capacityPercent]}
                onValueChange={handleSliderChange}
                min={0}
                max={100}
                step={5}
                className="w-full cursor-pointer"
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">0%</span>
                <span className={cn(
                  "text-lg font-bold tabular-nums",
                  capacityPercent > 80 ? "text-destructive" :
                  capacityPercent > 60 ? "text-amber-600 dark:text-amber-400" :
                  "text-foreground"
                )}>
                  {capacityPercent}%
                </span>
                <span className="text-xs text-muted-foreground">100%</span>
              </div>
              {!sliderTouched && (
                <p className="text-xs text-muted-foreground italic text-center">
                  Bewege den Slider, um deine Einschätzung abzugeben.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3 animate-in fade-in duration-200">
              <p className="text-xs text-muted-foreground">
                Mindestens ein Feld ausfüllen. Format: z.B. „3 von 5" für automatische %-Berechnung.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Entwickler (FTE)</Label>
                  <Input
                    value={absoluteValues.developers || ''}
                    onChange={e => setAbsoluteValues(prev => ({ ...prev, developers: e.target.value }))}
                    placeholder="z.B. 3 von 5"
                    className="text-sm bg-background"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Sales-Stunden/Woche</Label>
                  <Input
                    value={absoluteValues.salesHours || ''}
                    onChange={e => setAbsoluteValues(prev => ({ ...prev, salesHours: e.target.value }))}
                    placeholder="z.B. 40h"
                    className="text-sm bg-background"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Budget (€)</Label>
                  <Input
                    value={absoluteValues.budget || ''}
                    onChange={e => setAbsoluteValues(prev => ({ ...prev, budget: e.target.value }))}
                    placeholder="z.B. 120.000"
                    className="text-sm bg-background"
                  />
                </div>
              </div>
              {absoluteCapacityFilled && (
                <p className="text-xs text-muted-foreground text-center">
                  Berechnete Kapazitätsbindung: <span className="font-semibold text-foreground">{capacityPercent}%</span>
                </p>
              )}
            </div>
          )}

          {/* Capacity Warning */}
          {capacityPercent > 70 && capacityGateOk && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 animate-in fade-in duration-200">
              <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-sm text-destructive">
                Bei {capacityPercent}% Kapazität auf {labelA} bleibt <span className="font-semibold">kaum Spielraum</span> für {labelB}.
              </p>
            </div>
          )}
          {/* Capacity-Thesis Inconsistency */}
          {capacityInconsistency && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 animate-in fade-in duration-200">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700 dark:text-amber-300">
                {capacityInconsistency}
              </p>
            </div>
          )}
        </div>

        {/* Bias-Shift Mirror */}
        {biasShiftMessage && sliderTouched && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20 animate-in fade-in duration-200">
            <Eye className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <p className="text-sm text-foreground/80">
              {biasShiftMessage}
            </p>
          </div>
        )}

        {/* 2. KPI-Verlust */}
        <div className="space-y-3 p-4 rounded-xl border border-border bg-background">
          <Label className="text-sm font-semibold text-foreground">
            Welche messbare Kennzahl verschlechtert sich bei <span className="text-destructive">{labelB}</span>?
          </Label>

          {/* Stufe 1: System-empfohlene KPIs als Segment-Buttons */}
          <div className="grid grid-cols-3 gap-2">
            {suggestedKpiKeys.map(key => {
              const opt = KPI_OPTIONS.find(o => o.value === key);
              if (!opt) return null;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setKpiMetric(key); setShowAllKpis(false); }}
                  className={cn(
                    "py-2.5 px-3 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer text-center",
                    kpiMetric === key
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-background text-muted-foreground hover:border-border hover:bg-muted/30"
                  )}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {/* Stufe 2: Fallback-Dropdown */}
          {!showAllKpis && !suggestedKpiKeys.includes(kpiMetric) && kpiMetric === "" && (
            <button
              type="button"
              onClick={() => setShowAllKpis(true)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
            >
              Andere KPI wählen…
            </button>
          )}
          {(showAllKpis || (kpiMetric !== "" && !suggestedKpiKeys.includes(kpiMetric))) && (
            <div className="animate-in fade-in duration-200">
              <Select value={suggestedKpiKeys.includes(kpiMetric) ? "" : kpiMetric} onValueChange={(v) => { setKpiMetric(v); }}>
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="Andere KPI auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {KPI_OPTIONS.filter(o => !suggestedKpiKeys.includes(o.value)).map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {kpiMetric && (
            <div className="grid grid-cols-2 gap-3 animate-in fade-in duration-200">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Von (aktuell)</Label>
                <Input
                  value={kpiFrom}
                  onChange={e => setKpiFrom(e.target.value)}
                  placeholder="z.B. 15 Leads/Monat"
                  className="text-sm bg-background"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Auf (prognostiziert)</Label>
                <Input
                  value={kpiTo}
                  onChange={e => setKpiTo(e.target.value)}
                  placeholder="z.B. 5 Leads/Monat"
                  className="text-sm bg-background"
                />
              </div>
            </div>
          )}
        </div>

          {/* KPI-Risk Constraint Warning */}
          {kpiMetric && !isKpiLinked && riskType !== 'general' && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 animate-in fade-in duration-200">
              <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
              <div className="text-sm text-destructive">
                <p className="font-semibold">Dein Kippunkt testet nicht das dominante Risiko.</p>
                <p className="mt-1">Dominantes Risiko: <span className="font-medium">{RISK_TYPE_LABELS[riskType]}</span></p>
                <p className="mt-0.5">Erlaubte KPIs: {riskMapping.allowed.map(k => KPI_OPTIONS.find(o => o.value === k)?.label).filter(Boolean).join(", ")}</p>
              </div>
            </div>
          )}
        <div className="space-y-3 p-4 rounded-xl border border-border bg-background">
          <Label className="text-sm font-semibold text-foreground">
            In welchem Zeitraum wird dieser Verlust spürbar?
          </Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {TIMEFRAME_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setImpactTimeframe(opt.value)}
                className={cn(
                  "py-2.5 px-3 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer",
                  impactTimeframe === opt.value
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-background text-muted-foreground hover:border-border hover:bg-muted/30"
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

          {/* Timeframe Constraint Warning */}
          {timeframeConstraint.warning && (
            <div className={cn(
              "flex items-start gap-2 p-3 rounded-lg animate-in fade-in duration-200",
              timeframeConstraint.hardBlock
                ? "bg-destructive/10 border border-destructive/20"
                : "bg-amber-500/10 border border-amber-500/20"
            )}>
              <AlertTriangle className={cn("w-4 h-4 flex-shrink-0 mt-0.5", timeframeConstraint.hardBlock ? "text-destructive" : "text-amber-600 dark:text-amber-400")} />
              <p className={cn("text-sm", timeframeConstraint.hardBlock ? "text-destructive" : "text-amber-700 dark:text-amber-300")}>
                {timeframeConstraint.warning}
              </p>
            </div>
          )}
      </div>

      {/* ═══ Abschnitt 3: Spannungs-Score + Risiko-Kopplung ═══ */}
      {gateValid && (
        <div className={cn(
          "p-4 rounded-xl border-2 animate-in fade-in slide-in-from-bottom-2 duration-300",
          tensionLevel === "high" && "border-emerald-500/50 bg-emerald-500/10",
          tensionLevel === "medium" && "border-amber-500/50 bg-amber-500/10",
          tensionLevel === "low" && "border-destructive/50 bg-destructive/10",
        )}>
          <div className="flex items-start gap-3">
            {tensionLevel === "high" ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
            ) : tensionLevel === "medium" ? (
              <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            ) : (
              <TrendingDown className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className={cn(
                  "text-base font-semibold",
                  tensionLevel === "high" && "text-emerald-700 dark:text-emerald-300",
                  tensionLevel === "medium" && "text-amber-700 dark:text-amber-300",
                  tensionLevel === "low" && "text-destructive",
                )}>
                  Spannungsfeld: {tensionScore}/10
                </p>
                <Badge variant="outline" className={cn(
                  "text-xs gap-1",
                  riskAlignmentScore >= 3 && "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
                  riskAlignmentScore === 2 && "bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30",
                  riskAlignmentScore <= 1 && "bg-destructive/20 text-destructive border-destructive/30",
                )}>
                  <Shield className="w-3 h-3" />
                  Risiko-Kopplung: {riskAlignmentScore}/3
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                {tensionLevel === "high" && "Die Ressourcenbindung und der KPI-Verlust zeigen klaren Handlungsdruck."}
                {tensionLevel === "medium" && "Es gibt Spannung, aber die Dringlichkeit ist noch nicht maximal."}
                {tensionLevel === "low" && "Wenn keine echten Kosten entstehen, fehlt die strategische Notwendigkeit."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Gate-Status Hint */}
      {!gateValid && (
        <p className="text-sm text-muted-foreground text-center pt-2 border-t border-border/30">
          {!capacityGateOk 
            ? (capacityMode === 'percent' ? "Bewege den Kapazitäts-Slider" : "Fülle mindestens ein Kapazitätsfeld aus")
            : !kpiMetric ? "Wähle eine KPI"
            : !isKpiLinked ? "Die gewählte KPI passt nicht zum dominanten Risiko."
            : !kpiFrom.trim() || !kpiTo.trim() ? "Du kannst keinen Zielkonflikt haben ohne messbaren Verlust."
            : timeframeConstraint.hardBlock ? "Dein Zeitrahmen ist nicht operationalisierbar."
            : "Wähle einen Zeitrahmen"
          } um fortzufahren.
        </p>
      )}
    </div>
  );
};
